// Startup Detail View - displays company profile in read-only mode
// Fetches real data from backend using shared services
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StartupProfileForm,
  useFormSections,
  mapProfileToFormValues,
  mapProfileEditMetadata,
} from '@startus/startup-form';
import { fetchCompanyProfile, getErrorMessage, captureException } from '@startus/lib';
import { ProgressBar } from '@startus/ui';
import type { StartupFormData, PitchDocument, FieldMetadataMap } from '@startus/startup-form';
import type { AdminStartupItem } from '../types/startup';
import { STAGE_LABELS, STAGE_COLORS } from '../types/startup';

interface StartupDetailViewProps {
  startup: AdminStartupItem;
  onClose: () => void;
  onViewApplications?: () => void;
}

const StartupDetailView: React.FC<StartupDetailViewProps> = ({
  startup,
  onClose,
  onViewApplications,
}) => {
  const { t } = useTranslation('admin');
  // Fetch dynamic sections
  const { sections: dynamicSections, isLoading: sectionsLoading } = useFormSections();

  // Profile data state
  const [profileData, setProfileData] = useState<StartupFormData>({});
  const [fieldMetadata, setFieldMetadata] = useState<FieldMetadataMap>({});
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<unknown>(null);
  const [pitchDocuments, setPitchDocuments] = useState<PitchDocument[]>([]);

  // Fetch company profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!startup.id) {
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);
      setProfileError(null);

      try {
        const response = await fetchCompanyProfile(startup.id);
        const values = mapProfileToFormValues(response.data);
        setProfileData(values);
        setFieldMetadata(mapProfileEditMetadata(response.data));

        // Extract reference documents from profile_assets field (same logic as web)
        const fileField = response.data
          .flatMap((s) => s.fields)
          .find((f) => f.fieldName === 'profile_assets');
        const docs: PitchDocument[] = [];
        if (fileField?.value) {
          try {
            const urls: string[] = JSON.parse(fileField.value);
            urls.forEach((url) => {
              docs.push({
                id: url,
                type: /\.pdf$/i.test(url) ? 'pdf' : 'video',
                name: url.split('/').pop() ?? 'document',
                url,
                createdAt: fileField.updatedAt ?? new Date().toISOString(),
              });
            });
          } catch {
            /* value not a JSON array — no files */
          }
        }
        setPitchDocuments(docs);
      } catch (err) {
        captureException(err);
        setProfileError(err);
        console.error('[StartupDetailView] Failed to load profile:', err);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [startup.id]);

  const isLoading = sectionsLoading || profileLoading;

  // Overall completion % — same logic as web StartupProfilePage
  const globalProgress = useMemo(() => {
    if (!dynamicSections) return 0;
    const allFields = dynamicSections.flatMap((s) => s.fields);
    if (allFields.length === 0) return 0;
    const filled = allFields.filter((f) => {
      const val = profileData[f.name];
      if (val === undefined || val === null || val === '') return false;
      return Array.isArray(val) ? val.length > 0 : true;
    }).length;
    return Math.round((filled / allFields.length) * 100);
  }, [profileData, dynamicSections]);

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Sticky header — constrained to the same max-width as the form.
          Rounded top matches the card below; no bottom border so it reads as one block. */}
      <div className="sticky top-4 z-10 bg-[#1E1E1E] rounded-t-xl px-4 py-3 flex items-center gap-3 shadow-sm">
        {/* Back button */}
        <button
          className="btn btn-ghost btn-sm gap-1"
          onClick={onClose}
          aria-label={t('startups.modal.detail.backButton')}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="hidden sm:inline">{t('startups.modal.detail.backButton')}</span>
        </button>

        {/* Startup name + stage — centered between the two action slots */}
        <div className="flex-1 flex items-center justify-center gap-3 min-w-0">
          <h1 className="text-lg font-bold truncate">{startup.name}</h1>
          {startup.stage && (
            <span
              className={`badge badge-md shrink-0 ${STAGE_COLORS[startup.stage] ?? 'badge-ghost'}`}
            >
              {STAGE_LABELS[startup.stage] ?? startup.stage}
            </span>
          )}
        </div>

        {/* Secondary action — only rendered when available */}
        {onViewApplications && (
          <button className="btn btn-outline btn-sm shrink-0" onClick={onViewApplications}>
            <span className="hidden sm:inline">{t('startups.modal.detail.viewApplications')}</span>
            {/* Icon-only on mobile */}
            <svg
              className="w-4 h-4 sm:hidden"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Content — no extra padding; the outer mx-auto wrapper provides the gutter */}
      {/* Loading state */}
      {isLoading && (
        <div className="alert alert-info shadow-sm mb-4">
          <span className="loading loading-spinner loading-sm"></span>
          <span>{t('startups.modal.detail.loading')}</span>
        </div>
      )}

      {/* Error state */}
      {profileError != null && (
        <div role="alert" className="alert alert-error shadow-sm mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">
            {t('startups.modal.detail.errorPrefix')} {getErrorMessage(profileError, t)}
          </span>
        </div>
      )}

      {/* Form — rounded-b-xl closes the visual block opened by the sticky header */}
      {!isLoading && dynamicSections && (
        <div className="card bg-[#1E1E1E] rounded-t-none rounded-b-xl">
          <div className="card-body">
            <StartupProfileForm
              mode="view"
              sections={dynamicSections}
              defaultValues={profileData}
              fieldMetadata={fieldMetadata}
              renderTopRow={() => (
                <div className="grid grid-cols-2 gap-4 px-6 pt-6 pb-4">
                  {/* Left: red dot + startup name + progress bar */}
                  <div className="px-3 py-3 border border-white/5 rounded-xl flex flex-col justify-between min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
                      <h2 className="text-xl font-medium text-white leading-tight truncate">
                        {startup.name}
                      </h2>
                    </div>
                    <div className="ml-1 mt-3">
                      <p className="text-xs text-gray-400 mb-3 ml-5">
                        {t('startups.modal.detail.overallProgress', { value: globalProgress })}
                      </p>
                      <ProgressBar
                        value={globalProgress}
                        size="sm"
                        trackClass="bg-white/5"
                        className="ml-5"
                      />
                    </div>
                  </div>

                  {/* Right: reference documents — read-only in admin */}
                  <div className="border border-white/5 rounded-xl flex flex-col">
                    <div className="flex items-center gap-2 px-6 py-5">
                      <svg
                        className="w-4 h-4 text-gray-400 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-white">
                        {t('common:pitch.referenceDocument')}
                      </span>
                    </div>

                    <div className="px-6 pb-5 space-y-2 flex-1">
                      {pitchDocuments.length === 0 && <p className="text-xs text-gray-500">—</p>}
                      {pitchDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-2">
                          {doc.type === 'video' ? (
                            <svg
                              className="w-4 h-4 text-red-500 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4 text-red-500 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                          <span className="text-xs text-gray-400 truncate flex-1">{doc.name}</span>
                          <span className="text-xs text-gray-500 shrink-0">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StartupDetailView;
